/**
 * SOVRANOVA v1 — App Shell
 * Views: dashboard | session | vent | journal | sleep | assessment | hypnosis | affirmations
 */
import { useState, useEffect } from 'react'
import { stopAllAudio } from './services/audioBus'
import { useAuth } from './hooks/useAuth'
import { api } from './services/api'
import { SettingsProvider } from './hooks/useSettings'
import { AuthView }       from './components/auth/AuthView'
import { ResetPasswordView } from './components/auth/ResetPasswordView'
import { arrivedFromRecoveryLink } from './services/authUrlState'
import { OnboardingFlow } from './components/onboarding/OnboardingFlow'
import { InstallPrompt } from './components/pwa/InstallPrompt'
import { SessionView }    from './components/session/SessionView'
import { Dashboard }      from './components/dashboard/Dashboard'
import { AssessmentFlow } from './components/outcomes/AssessmentFlow'
import { VentView }       from './components/session/VentView'
import { JournalView }    from './components/journal/JournalView'
import { SleepPlayer }    from './components/session/SleepPlayer'
import { HypnosisHub }    from './components/hypnosis/HypnosisHub'
import { AffirmationsView } from './components/affirmations/AffirmationsView'
import { SettingsPanel }  from './components/settings/SettingsPanel'
import { MasculineView }  from './components/masculine/MasculineView'
import { FeminineView }   from './components/feminine/FeminineView'
import { SovereignFlowView } from './components/sovereign_flow/SovereignFlowView'
import { AnchorView }      from './components/anchor/AnchorView'
import { IntimacyView }    from './components/intimacy/IntimacyView'
import { AboutView }        from './components/about/AboutView'
import { GuideView }        from './components/about/GuideView'
import { FamilyView }      from './components/family/FamilyView'
import { MetabolicView }  from './components/metabolic/MetabolicView'
import { FocusView }      from './components/focus/FocusView'
import { HandoverView }   from './components/handover/HandoverView'
import { ReferralView }   from './components/referral/ReferralView'
import { LiveCompanionView } from './components/live/LiveCompanionView'
import { MeditationHub }   from './components/meditation/MeditationHub'
import { CouplesView }     from './components/couples/CouplesView'
import { AmbientView }     from './components/ambient/AmbientView'
import { VitalityView }    from './components/vitality/VitalityView'

// Adult (18+) Intimacy module — OFF unless explicitly enabled at build time.
// Stays invisible (no route, no menu entry) in the production kids-inclusive
// build until VITE_FEATURE_INTIMACY='true'. See SideMenu for the gated entry.
const FEATURE_INTIMACY = import.meta.env.VITE_FEATURE_INTIMACY === 'true'
// Metabolic (calm eating + optional fasting) — OFF unless enabled at build time.
const FEATURE_METABOLIC = import.meta.env.VITE_FEATURE_METABOLIC === 'true'
// FOCUS (ADHD executive-function micro-step planner) — OFF unless enabled at build time.
const FEATURE_FOCUS = import.meta.env.VITE_FEATURE_FOCUS === 'true'
// MEDITATION (mindfulness meditation) — OFF unless enabled at build time.
const FEATURE_MEDITATION = import.meta.env.VITE_FEATURE_MEDITATION === 'true'
// VITALITY (movement & fitness by age/body — general wellness, not physiotherapy)
// — OFF unless enabled at build time. Mirrors the backend FEATURE_VITALITY gate.
const FEATURE_VITALITY = import.meta.env.VITE_FEATURE_VITALITY === 'true'
// COUPLES (solo-guided relationship practice) — OFF unless enabled at build time.
const FEATURE_COUPLES = import.meta.env.VITE_FEATURE_COUPLES === 'true'
// AMBIENT (Wave 1 — session/conflict debrief → reflection) — OFF unless enabled at build time.
const FEATURE_AMBIENT = import.meta.env.VITE_FEATURE_AMBIENT === 'true'
// ENGINE (Sprint-2 — clinician-gated psychoeducation modules via /api/engine/*)
// — OFF unless enabled at build time. Mirrors the backend's own ENGINE_ENABLED
// gate: without it, no session is ever started and no engine traffic occurs.
const FEATURE_ENGINE = import.meta.env.VITE_FEATURE_ENGINE === 'true'
// REVIEW CONSOLE (Sprint-2 Task 6 — clinician approve/reject queue via
// /api/engine/clinician/*) — OFF unless enabled at build time. Even when on,
// the backend's own CLINICIAN_EMAILS allowlist gate still decides whether a
// given signed-in account actually gets data back; this flag only controls
// whether the console is reachable from the side menu at all.
const FEATURE_REVIEW_CONSOLE = import.meta.env.VITE_FEATURE_REVIEW_CONSOLE === 'true'
// FIND PROFESSIONAL SUPPORT (Sprint-2 S3 W11 — referral directories v0, via
// /api/referral/*) — OFF unless enabled at build time. Mirrors the backend's
// own FEATURE_REFERRAL gate: without it, no referral traffic ever occurs.
const FEATURE_REFERRAL = import.meta.env.VITE_FEATURE_REFERRAL === 'true'
// FEEDBACK (in-app, worldwide, voice-or-text) — matches SideMenu's gate and the
// backend FEATURE_FEEDBACK mount. Off = view unreachable, bundle unchanged.
const FEATURE_FEEDBACK = import.meta.env.VITE_FEATURE_FEEDBACK === 'true'
// SLEEP PROTOCOL (8-hour 432/434 Hz tone, SleepPlayer) — OFF unless enabled at
// build time. Disabled for beta wave 1 (pre-beta audio/claims audit, 4 Sep 2026,
// founder decision D-A): the screen made unsupported "entrainment / deep sleep
// architecture" claims. Off = no menu entry, no route; a stale 'sleep' view
// falls through to the Dashboard. Scripted sleep modes are unaffected.
const FEATURE_SLEEP_PROTOCOL = import.meta.env.VITE_FEATURE_SLEEP_PROTOCOL === 'true'
// FAMILY / parental controls (guardian linking, managed accounts) — OFF for
// beta wave 1 (4 Sep 2026). Wave 1 is 18+ only and closed, so the linking and
// managed-account surfaces have no audience yet, no member-consent step exists,
// and the invite flow has not been through the Individual/VENT audit's Family
// findings. Off = no menu entry and no route; the view falls through to the
// Dashboard. This does NOT touch /api/family/me/entitlements below — parental
// blocks must keep being ENFORCED while the management UI is hidden. Pairs with
// FEATURE_FAMILY in server.js.
const FEATURE_FAMILY = import.meta.env.VITE_FEATURE_FAMILY === 'true'
// Live Companion (full-duplex Hume EVI voice) — OFF for beta wave 1 (4 Sep
// 2026). Hides the route, the nav tab and the Dashboard card; the backend
// unmounts /api/live and /api/hume-clm under FEATURE_LIVE_COMPANION, so a
// stale bundle or a direct call fails closed too.
const FEATURE_LIVE_COMPANION = import.meta.env.VITE_FEATURE_LIVE_COMPANION === 'true'
// Hume EVI voice — CONTAINED for beta wave 1 (H1, 16 Sep 2026). MASCULINE,
// FEMININE and SOVEREIGN FLOW are Hume-only views: with the flag off a stale
// or direct entry falls through to the Dashboard shell below. The privacy
// boundary is the backend (FEATURE_HUME_VOICE refuses every Hume token).
const FEATURE_HUME_VOICE = import.meta.env.VITE_FEATURE_HUME_VOICE === 'true'
import { SideMenu }       from './components/nav/SideMenu'
import FeedbackView       from './components/feedback/FeedbackView'
import { EngineView }     from './components/engine/EngineView'
import { ReviewConsole }  from './components/review/ReviewConsole'

function AppInner() {
  const { user, loading: authLoading, signOut, passwordRecovery } = useAuth()
  const [view,              setView]              = useState('dashboard')
  const [onboardingComplete, setOnboardingComplete] = useState(null)
  const [profileLoading,    setProfileLoading]    = useState(true)
  const [showSettings,      setShowSettings]      = useState(false)
  const [menuOpen,          setMenuOpen]          = useState(false)
  const [blocked,           setBlocked]           = useState(() => new Set())
  const [sessionSeed,       setSessionSeed]       = useState(null)  // Ambient → AI Session handoff

  // ONE VOICE AT A TIME. Every view change silences whatever is still speaking.
  // Without this, walking through features leaves each one's voice running
  // underneath the next — they stack up and talk over each other.
  // spareProtected: the once-daily welcome greeting may finish across a
  // navigation (its first tap of the day often IS a navigation — cutting it
  // off mid-word made the welcome useless, 19 Aug). It still yields the
  // moment any other voice starts — see audioBus.registerAudio.
  useEffect(() => { stopAllAudio(null, { spareProtected: true }) }, [view])

  // Navigate from the side menu. 'settings' opens the panel; everything else
  // is a view. Modules not yet built are disabled in the menu so won't arrive here.
  const navigate = (target) => {
    if (target === 'settings') { setShowSettings(true); return }
    setView(target)
  }

  // ONBOARDING GATE — hardened 19 Aug after the live loop. The old shape
  // (`.catch(() => setOnboardingComplete(false))`) treated ANY failed profile
  // read — a token mid-refresh, a network blip, a 500 during a deploy — as
  // "this person has never onboarded", and re-ran the whole 8-step flow for a
  // user whose DB row said onboarding_complete=true the entire time. It also
  // re-ran on every auth token refresh (the `user` object identity changes),
  // so one bad fetch an hour was enough to keep kicking a real user back to
  // "First things first". Rules now:
  //   1. Once ANY source has said "complete" (server, or this device's own
  //      record of finishing the flow), never regress to onboarding.
  //   2. A failed read is retried, then resolved in favour of showing the
  //      app — a missing questionnaire is a far smaller harm than a loop.
  //   3. Keyed on user.id, not the user object, so refreshes don't re-gate.
  useEffect(() => {
    if (!user) { setProfileLoading(false); setOnboardingComplete(null); return }
    let alive = true
    const localKey = `sov_onboarded:${user.id}`
    let localDone = false
    try { localDone = localStorage.getItem(localKey) === 'true' } catch (_) {}
    if (localDone) { setOnboardingComplete(true); setProfileLoading(false) }
    const attempt = (n) => {
      api.get('/auth/profile')
        .then(({ profile }) => {
          if (!alive) return
          const done = profile?.onboarding_complete === true
          if (done) { try { localStorage.setItem(localKey, 'true') } catch (_) {} }
          setOnboardingComplete(done || localDone)
          // Persist region so the always-on (offline) CrisisLifeline shows the
          // correct local crisis numbers for this user.
          try { if (profile?.region) localStorage.setItem('sov_region', profile.region) } catch (_) {}
          setProfileLoading(false)
        })
        .catch(() => {
          if (!alive) return
          if (n < 2) { setTimeout(() => attempt(n + 1), 1200 * (n + 1)); return }
          // Unreadable after retries: open the app. If this user truly never
          // onboarded, the next successful profile read shows the flow.
          setOnboardingComplete(true)
          setProfileLoading(false)
        })
    }
    attempt(0)
    return () => { alive = false }
  }, [user?.id])

  // Parental controls: a guardian can switch specific modules off for a managed
  // (e.g. child) account. Enforced here — a blocked module can't be opened.
  useEffect(() => {
    if (!user) { setBlocked(new Set()); return }
    api.get('/family/me/entitlements')
      .then(r => setBlocked(new Set((r?.modules || []).filter(m => m.allowed === false).map(m => m.moduleId))))
      .catch(() => setBlocked(new Set()))
  }, [user])

  if (authLoading || profileLoading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', flexDirection:'column', gap:'1rem' }}>
        <div style={{ fontSize:'2.5rem', opacity:0.25 }}>◎</div>
        <p style={{ color:'#999', fontSize:'0.875rem' }}>Loading...</p>
      </div>
    )
  }

  // ── Password recovery ─────────────────────────────────────────────────────
  // A reset link signs the person in, so without these two lines they would
  // land on the dashboard — or, for an account that never finished signing up,
  // in the onboarding questionnaire — and never be offered the password change
  // they clicked the link for. This sits ABOVE the onboarding gate deliberately.
  // While the link is still being exchanged for a session, `user` is briefly
  // null; showing the sign-in card in that gap would look like the link failed,
  // so we hold the loading state until Supabase has finished.
  if (!user && arrivedFromRecoveryLink() && passwordRecovery) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', flexDirection:'column', gap:'1rem' }}>
        <div style={{ fontSize:'2.5rem', opacity:0.25 }}>◎</div>
        <p style={{ color:'#999', fontSize:'0.875rem' }}>Opening your reset link…</p>
      </div>
    )
  }
  if (!user)                     return <AuthView />
  if (passwordRecovery)          return <ResetPasswordView />
  if (onboardingComplete === false) return <OnboardingFlow onComplete={() => {
    // Record completion ON THIS DEVICE too — if the server save raced a token
    // refresh and failed, the person must still never be re-asked (19 Aug loop).
    try { localStorage.setItem(`sov_onboarded:${user.id}`, 'true') } catch (_) {}
    setOnboardingComplete(true)
  }} />

  // Parental block: a guardian turned this space off for this account.
  if (blocked.has(view)) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem', padding:'2rem', textAlign:'center' }}>
        <div style={{ fontSize:'2rem', opacity:0.4 }}>🔒</div>
        <h2 style={{ margin:0, color:'#444' }}>Turned off</h2>
        <p style={{ color:'#888', maxWidth:380, lineHeight:1.6 }}>This space is switched off in your account's parental settings. A guardian can change this from the family dashboard.</p>
        <button className="nav-btn" onClick={() => setView('dashboard')}>← Back to Journey</button>
      </div>
    )
  }

  // Full-screen views — no nav
  if (view === 'assessment')   return <AssessmentFlow onComplete={() => setView('dashboard')} />
  if (view === 'vent')         return <VentView    onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'live' && FEATURE_LIVE_COMPANION) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ width:'100%', maxWidth:520 }}>
        <LiveCompanionView onClose={() => setView('dashboard')} />
      </div>
    </div>
  )
  if (view === 'journal')      return <JournalView onClose={() => setView('dashboard')} />
  if (view === 'sleep' && FEATURE_SLEEP_PROTOCOL) return <SleepPlayer hours={8} onClose={() => setView('dashboard')} />
  if (view === 'hypnosis')     return <HypnosisHub onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'affirmations') return <AffirmationsView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'feedback' && FEATURE_FEEDBACK) return <FeedbackView onHome={() => setView('dashboard')} />
  if (view === 'masculine' && FEATURE_HUME_VOICE)    return <MasculineView userName={undefined} onClose={() => setView('dashboard')} onStartPhysical={() => setView('session')} />
  if (view === 'feminine' && FEATURE_HUME_VOICE)     return <FeminineView userName={undefined} onClose={() => setView('dashboard')} onStartPhysical={() => setView('session')} />
  if (view === 'sovereign_flow' && FEATURE_HUME_VOICE) return <SovereignFlowView userName={undefined} onClose={() => setView('dashboard')} />
  if (view === 'anchor')       return <AnchorView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'intimacy' && FEATURE_INTIMACY) return <IntimacyView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'about')        return <AboutView onClose={() => setView('dashboard')} />
  if (view === 'guide')        return <GuideView onClose={() => setView('dashboard')} onNavigate={(v) => setView(v)} />
  if (view === 'handover')     return <HandoverView onClose={() => setView('dashboard')} />
  if (view === 'referral' && FEATURE_REFERRAL) return <ReferralView onClose={() => setView('dashboard')} onNavigate={(v) => setView(v)} />
  // Family / guardian linking is OFF for beta wave 1 (4 Sep 2026): with the
  // flag off a direct entry falls through to the dashboard shell below, and
  // the API is unmounted server-side (FEATURE_FAMILY).
  if (view === 'family' && FEATURE_FAMILY) return <FamilyView onClose={() => setView('dashboard')} />
  if (view === 'metabolic' && FEATURE_METABOLIC) return <MetabolicView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'focus' && FEATURE_FOCUS) return <FocusView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'meditation' && FEATURE_MEDITATION) return <MeditationHub onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'vitality' && FEATURE_VITALITY) return <VitalityView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'couples' && FEATURE_COUPLES) return <CouplesView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} />
  if (view === 'ambient' && FEATURE_AMBIENT) return <AmbientView onClose={() => setView('dashboard')} onHome={() => setView('dashboard')} onOpenSession={(s) => { setSessionSeed(s); setView('session') }} />
  if (view === 'engine' && FEATURE_ENGINE) return <EngineView onClose={() => setView('dashboard')} />
  if (view === 'review-console' && FEATURE_REVIEW_CONSOLE) return <ReviewConsole onClose={() => setView('dashboard')} />

  return (
    <div className="app-shell">
      {/* Beta install sheet — renders nothing once installed or dismissed. */}
      <InstallPrompt />
      <header className="app-header">
        <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
          <button className="nav-btn" onClick={() => setMenuOpen(true)} title="Menu" style={{ fontSize:'1.2rem', lineHeight:1 }}>☰</button>
          <div className="app-brand">◎ SOVRANOVA</div>
        </div>
        <nav className="app-nav">
          <button className={`nav-btn ${view === 'dashboard' ? 'active' : ''}`} onClick={() => setView('dashboard')}>Journey</button>
          <button className={`nav-btn ${view === 'session'   ? 'active' : ''}`} onClick={() => { setSessionSeed(null); setView('session') }}>Session</button>
          {FEATURE_LIVE_COMPANION && (
            <button className={`nav-btn ${view === 'live' ? 'active' : ''}`} onClick={() => setView('live')}>Live</button>
          )}
          <button className={`nav-btn ${view === 'hypnosis'  ? 'active' : ''}`} onClick={() => setView('hypnosis')}>Guided</button>
          <button className={`nav-btn ${view === 'vent'      ? 'active' : ''}`} onClick={() => setView('vent')}>Vent</button>
          <button className={`nav-btn ${view === 'journal'   ? 'active' : ''}`} onClick={() => setView('journal')}>Journal</button>
          <button className="nav-btn" onClick={() => setShowSettings(true)} title="Settings">⚙</button>
          <button className="nav-btn" onClick={signOut}>Sign Out</button>
        </nav>
      </header>

      <main className="app-content">
        {view === 'session' ? (
          <SessionView seed={sessionSeed} onSessionComplete={() => { setSessionSeed(null); setView('dashboard') }} onNavigate={(v) => setView(v)} />
        ) : (
          <Dashboard
            onStartSession={() => { setSessionSeed(null); setView('session') }}
            onStartAssessment={() => setView('assessment')}
            onStartVent={() => setView('vent')}
            onStartJournal={() => setView('journal')}
            onStartSleep={() => setView('sleep')}
            onStartHypnosis={() => setView('hypnosis')}
            onStartAffirmations={() => setView('affirmations')}
            {...(FEATURE_LIVE_COMPANION ? { onStartLive: () => setView('live') } : {})}
          />
        )}
      </main>

      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} onNavigate={navigate} currentView={view} blocked={blocked} />
    </div>
  )
}

export default function App() {
  return (
    <SettingsProvider>
      <AppInner />
    </SettingsProvider>
  )
}
